import { createApplication, isOTPResponse, toOTPSearch } from 'merchant-api';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { BlueBlob, Button, Card, formatPhoneNumber, FormattedMessageDiv, FormInput, FormMoneyInput, FormWrapper } from '@odmonorepo/core-components';
import { Col, Row, Stack } from 'react-bootstrap';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useAnalytics } from 'use-analytics';
import { markIntakeBridge } from './intakeBridge';

export const BasicInfoSchema = yup.object().shape({
  firstName:
    yup
    .string()
    .trim()
    .required('formInput.firstName.error')
    .matches(/^[A-Za-z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/, 'formInput.name.invalid'),

  lastName:
    yup
    .string()
    .trim()
    .required('formInput.lastName.error')
    .matches(/^[A-Za-z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/, 'formInput.name.invalid'),

  email:
    yup.string()
    .required('formInput.email.error')
    .email('formInput.email.invalid'),
  homePhone:
    yup
    .string()
    .required('formInput.homePhone.error')
    .test(
      'is-10-digits',
      'formInput.homePhone.invalid',
      val => val?.replace(/\D/g, '').length === 10
    ),
  grossAnnualRevenue:
    yup
    .number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('formInput.grossAnnualRevenue.error')
    .test(
      'divisible-by-1000',
      'formInput.grossAnnualRevenue.invalid',
      val => val !== undefined && val % 1000 === 0
    ),
});

function getReferralSourceFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('referralSource');
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  } catch {
    return null;
  }
}

function getOfferCheckSumFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('offerCheckSum');
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  } catch {
    return null;
  }
}

export type BasicInfoDefaults = Partial<{
  firstName: string;
  lastName: string;
  email: string;
  homePhone: string;
}>;

export interface BasicInfoProps {
  defaultValues?: BasicInfoDefaults;
  // Only needed for the Prove opt-out case: set false there, since Prove already logged step 1.
  emitStepLoaded?: boolean;
}

const basicInfoError = new Error('Unable to create application');

export const BasicInfo = ({ defaultValues, emitStepLoaded = true }: BasicInfoProps = {}) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const referralSourceFromQuery = useMemo(() => getReferralSourceFromUrl(), []);
  const offerCheckSumFromQuery = useMemo(() => getOfferCheckSumFromUrl(), []);

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: createApplication,
    onSuccess: (data) => {
      markIntakeBridge();
      if (isOTPResponse(data)) {
        return navigate({to: '/login/otp', search: toOTPSearch(data)});
      }

      return navigate({to: '/application/$id/apply', params: { id: data.id }});
    },
  });

  const handleSubmit = async (values: any) => {
    if (isPending) return;
    const referralSourceToUse = referralSourceFromQuery ?? window.location.href;
    const data = {
      ...values,
      referralSource: referralSourceToUse,
      channelCode: 'DIRECT',
      ...(offerCheckSumFromQuery ? { offerCheckSum: offerCheckSumFromQuery } : {}),
    };
    await mutateAsync(data).catch(() => undefined);
  };

  useEffect(() => {
    // Legacy top-of-funnel event: always emit so opt-out sessions stay in legacy reporting.
    analytics.track('FirstStepLoaded', {
      fromStep: 'BASIC_INFO'
    });
    if (!emitStepLoaded) return;
    analytics.track('StepLoaded', { step: 'BASIC_INFO', stepOrder: 1, flowType: 'direct_new' });
  }, []);

  if (error) throw error instanceof Error ? error : basicInfoError;

  return (
     <FormWrapper
      schema={BasicInfoSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
        <Stack gap={6} style={{maxWidth: '600px', margin: 'auto'}}>
          <BlueBlob>
            <FormattedMessageDiv className="h3" id="basicInfo.title" />
          </BlueBlob>
          <Card>
            <Stack gap={5}>
              <h4 className="mb-0">
                <FormattedMessageDiv id={'basicInfo.cardTitle'}/>
              </h4>
              <Row>
                <Col>
                  <FormInput name="firstName" data-testid="firstName" useYupErrors />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput name="lastName" data-testid="lastName" useYupErrors/>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput name="email" data-testid="email" useYupErrors />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput name="homePhone" data-testid="homePhone" formatValue={formatPhoneNumber}   controlProps={{ inputMode: 'tel' }} useYupErrors />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormMoneyInput name="grossAnnualRevenue"
                  className="label-expanded"
                  data-testid="grossAnnualRevenue"
                  useYupErrors
                  helperText={<FormattedMessageDiv id="basicInfo.grossAnnualRevenue.subtext" />} />
                </Col>
              </Row>
            </Stack>
          </Card>
          <Button loading={isPending} disabled={isPending} type="submit" style={{ width: '100%', maxWidth: '367px', margin: '0 auto' }}>Continue</Button>
          <div>
           <FormattedMessageDiv
              className="fs-8"
              id="basicInfo.disclaimer.submit"
              values={{
                terms: (txt: any) => <a target="_blank" href="https://www.ondeck.com/terms">{txt}</a>,
                privacy: (txt: any) => <a target="_blank" href={`https://www.ondeck.com/privacy`}>{txt}</a>
              }}
            />
          </div>
        </Stack>
    </FormWrapper>
  );
}

export default BasicInfo;
